import React, { useCallback, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";

import InputError, { ERROR_PLACE } from "components/InputsField/InputError";
import FormikTextField from "components/InputsField/formikInputs/FormikTextField";
import Button from "components/common/Button";
import { SecurityService } from "service/SecurityService";
import { sendAim } from "helpers/statisticAim";

import styles from "./styles.module.scss";

const CodeForm = ({ isCodeCreated, setIsCodeCreated }) => {
    const lang = useSelector((state) => state.settings.lang);
    const { values } = useFormikContext();
    const { t } = useTranslation();

    const [error, setError] = useState(null);
    const [isCodeLoading, setIsCodeLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const timerRef = useRef(null);

    useEffect(() => {
        if (cooldown > 0) {
            timerRef.current = setTimeout(() => {
                setCooldown((prev) => prev - 1);
            }, 1000);
        }
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [cooldown]);

    const handleRequestCode = useCallback(() => {
        setIsCodeLoading(true);
        SecurityService.generateCode({
            email: values.email,
            lang,
            name: values.firstname,
        })
            .then(() => {
                setError(null);
                setIsCodeCreated(true);
                sendAim("code_reg");
                setCooldown(30);
            })
            .catch((e) => {
                console.log(e.response.data);
                setError(e.response.data.errors[0]);
            })
            .finally(() => {
                setIsCodeLoading(false);
            });
    }, [values.email, setIsCodeCreated, lang, values.firstname]);

    return (
        <div>
            <p className={styles.codeTitle}>{t("registration.validation_heading")}</p>
            <p className={styles.codeDescription}>{t("registration.validation_text")}</p>
            {isCodeCreated && (
                <>
                    <FormikTextField
                        name="code"
                        maxLength="32"
                        wrapperClassName={isCodeCreated ? styles["field--with-margin"] : ""}
                        additionalComponent={
                            cooldown === 0 && (
                                <button
                                    className={styles.refreshCode}
                                    onClick={handleRequestCode}
                                    disabled={isCodeLoading}
                                />
                            )
                        }
                        errorMode={ERROR_PLACE.none}
                        required
                    />
                    {cooldown > 0 && (
                        <p className={styles.cooldownText}>
                            {t("registration.resend_code_cooldown", { seconds: cooldown })}
                        </p>
                    )}
                </>
            )}
            {error && (
                <InputError
                    message={`${t(`registration.${error.path}`)}: ${t(error.type)}`}
                    mode={ERROR_PLACE.static}
                />
            )}
            {!isCodeCreated && (
                <div className={styles.button}>
                    <Button type="button" onClick={handleRequestCode} disabled={isCodeLoading}>
                        {t("registration.get_code_button")}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default CodeForm;
